import React, { useState, useRef } from 'react';
import { Button } from '../../components';
import {
  PitchSubmissionDoc,
  presignDeck,
  saveDeck,
  deleteDeck,
} from '../../services/pitch-submission-service';

const MARKET_SIZE   = ['Under $1M', '$1M–$10M', '$10M–$100M', '$100M–$1B', 'Over $1B'];
const INDUSTRY      = ['EdTech', 'FinTech', 'HealthTech', 'AgriTech', 'E-Commerce', 'SaaS', 'Marketplace', 'Clean Energy', 'Logistics', 'Media', 'Other'];
const STAGE         = ['Idea', 'MVP', 'Early Traction', 'Growth', 'Scale'];
const REVENUE_MODEL = ['SaaS', 'Marketplace', 'Transactional', 'Advertising', 'Hardware', 'Services', 'Consulting', 'Other'];
const TEAM_SIZE     = ['Solo', '2–3', '4–6', '7–10', '10+'];
const FUNDING_STAGE = ['Pre-seed', 'Seed', 'Series A', 'Series B+'];
const EQUITY        = ['5%', '7.5%', '10%', '12.5%', '15%', '20%', '25%', 'Other'];
const GEO           = ['Pakistan', 'South Asia', 'MENA', 'Global', 'Other'];

const ACCEPTED_DECK = '.pdf,.ppt,.pptx,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation';

interface PitchFormProps {
  type: 'initial' | 'final';
  data: PitchSubmissionDoc;
  readOnly?: boolean;
  onSaveDraft: (data: PitchSubmissionDoc) => Promise<void>;
  onSubmit?: (data: PitchSubmissionDoc) => Promise<void>;
  saving: boolean;
}

const inputClass = 'w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm placeholder:text-muted focus:outline-none focus:border-chart/50 disabled:opacity-60 disabled:cursor-not-allowed';
const labelClass = 'block text-xs font-medium text-muted mb-1';
const sectionClass = 'space-y-4';
const headingClass = 'font-display text-lg tracking-wide text-text uppercase mb-4';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {children}
  </div>
);

const Select: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: string[];
  disabled?: boolean;
  placeholder?: string;
}> = ({ value, onChange, options, disabled, placeholder = 'Select…' }) => (
  <select
    value={value}
    onChange={e => onChange(e.target.value)}
    disabled={disabled}
    className={inputClass}
  >
    <option value="">{placeholder}</option>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

const Textarea: React.FC<{
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
  rows?: number;
}> = ({ value, onChange, disabled, placeholder, rows = 3 }) => (
  <textarea
    value={value}
    onChange={e => onChange(e.target.value)}
    disabled={disabled}
    placeholder={placeholder}
    rows={rows}
    className={`${inputClass} resize-none`}
  />
);

const formatBytes = (bytes: number) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const PitchForm: React.FC<PitchFormProps> = ({ type, data, readOnly, onSaveDraft, onSubmit, saving }) => {
  const [local, setLocal] = useState<PitchSubmissionDoc>(data);
  const [deckUploading, setDeckUploading] = useState(false);
  const [deckDeleting, setDeckDeleting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const set = (field: keyof PitchSubmissionDoc) => (value: string) =>
    setLocal(prev => ({ ...prev, [field]: value }));

  const handleDeckUpload = async (file: File) => {
    setDeckUploading(true);
    try {
      const { presignedUrl, fileKey, fileUrl } = await presignDeck(type, file.name, file.type);
      await fetch(presignedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      const updated = await saveDeck(type, { fileKey, fileUrl, fileName: file.name, fileSize: file.size });
      setLocal(prev => ({
        ...prev,
        slideDeckUrl: updated.slideDeckUrl,
        slideDeckKey: updated.slideDeckKey,
        slideDeckName: updated.slideDeckName,
        slideDeckSize: updated.slideDeckSize,
      }));
    } catch {
      // upload failed silently — user can retry
    } finally {
      setDeckUploading(false);
    }
  };

  const handleDeckDelete = async () => {
    setDeckDeleting(true);
    try {
      await deleteDeck(type);
      setLocal(prev => ({ ...prev, slideDeckUrl: '', slideDeckKey: '', slideDeckName: '', slideDeckSize: 0 }));
    } catch {} finally {
      setDeckDeleting(false);
    }
  };

  return (
    <div className="space-y-10">
      {/* Read-only banner */}
      {readOnly && (
        <div className="flex items-center gap-2.5 px-4 py-3 bg-warning/10 border border-warning/20 rounded-lg text-sm text-warning">
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          This pitch is submitted and preserved as a permanent record.
        </div>
      )}

      {/* Section: Identity */}
      <section className={sectionClass}>
        <h3 className={headingClass}>Your Startup</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Startup Name">
            <input
              type="text"
              value={local.startupName}
              onChange={e => set('startupName')(e.target.value)}
              disabled={readOnly}
              placeholder="What's your startup called?"
              className={inputClass}
            />
          </Field>
          <Field label="Website">
            <input
              type="text"
              value={local.website}
              onChange={e => set('website')(e.target.value)}
              disabled={readOnly}
              placeholder="https://yourstartup.com"
              className={inputClass}
            />
          </Field>
        </div>
      </section>

      {/* Section: The Opportunity */}
      <section className={sectionClass}>
        <h3 className={headingClass}>The Opportunity</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Problem Statement">
            <Textarea value={local.problem} onChange={set('problem')} disabled={readOnly}
              placeholder="What problem are you solving?" />
          </Field>
          <Field label="Solution">
            <Textarea value={local.solution} onChange={set('solution')} disabled={readOnly}
              placeholder="How does your product solve it?" />
          </Field>
          <Field label="Target Market">
            <Textarea value={local.targetMarket} onChange={set('targetMarket')} disabled={readOnly}
              placeholder="Who are your customers?" rows={2} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:col-span-1">
            <Field label="Market Size">
              <Select value={local.marketSize} onChange={set('marketSize')} options={MARKET_SIZE} disabled={readOnly} />
            </Field>
            <Field label="Industry">
              <Select value={local.industry} onChange={set('industry')} options={INDUSTRY} disabled={readOnly} />
            </Field>
            <Field label="Geographic Focus">
              <Select value={local.geographicFocus} onChange={set('geographicFocus')} options={GEO} disabled={readOnly} />
            </Field>
          </div>
        </div>
      </section>

      {/* Section: The Business */}
      <section className={sectionClass}>
        <h3 className={headingClass}>The Business</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Stage">
              <Select value={local.stage} onChange={set('stage')} options={STAGE} disabled={readOnly} />
            </Field>
            <Field label="Revenue Model">
              <Select value={local.revenueModel} onChange={set('revenueModel')} options={REVENUE_MODEL} disabled={readOnly} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Team Size">
              <Select value={local.teamSize} onChange={set('teamSize')} options={TEAM_SIZE} disabled={readOnly} />
            </Field>
            <div /> {/* spacer */}
          </div>
          <div className="md:col-span-2">
            <Field label="Traction">
              <Textarea value={local.traction} onChange={set('traction')} disabled={readOnly}
                placeholder="Key metrics, revenue, users, pilots…" />
            </Field>
          </div>
        </div>
      </section>

      {/* Section: The Ask */}
      <section className={sectionClass}>
        <h3 className={headingClass}>The Ask</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="grid grid-cols-3 gap-4">
            <Field label="Funding Stage">
              <Select value={local.fundingStage} onChange={set('fundingStage')} options={FUNDING_STAGE} disabled={readOnly} />
            </Field>
            <Field label="Amount Sought">
              <input
                type="text"
                value={local.fundingAsk}
                onChange={e => set('fundingAsk')(e.target.value)}
                disabled={readOnly}
                placeholder="e.g. $200,000"
                className={inputClass}
              />
            </Field>
            <Field label="Equity Offered">
              <Select value={local.equityOffered} onChange={set('equityOffered')} options={EQUITY} disabled={readOnly} />
            </Field>
          </div>
          <Field label="Use of Funds">
            <Textarea value={local.useOfFunds} onChange={set('useOfFunds')} disabled={readOnly}
              placeholder="How will you deploy the capital?" />
          </Field>
        </div>
      </section>

      {/* Section: Competitive Edge */}
      <section className={sectionClass}>
        <h3 className={headingClass}>Competitive Edge</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Competitors">
            <Textarea value={local.competitors} onChange={set('competitors')} disabled={readOnly}
              placeholder="Who else is solving this problem?" />
          </Field>
          <Field label="Differentiation">
            <Textarea value={local.differentiation} onChange={set('differentiation')} disabled={readOnly}
              placeholder="What makes you uniquely positioned to win?" />
          </Field>
        </div>
      </section>

      {/* Section: Roadmap */}
      <section className={sectionClass}>
        <h3 className={headingClass}>Roadmap</h3>
        <Field label="12-Month Milestones">
          <Textarea value={local.milestones} onChange={set('milestones')} disabled={readOnly}
            placeholder="What will you achieve in the next 12 months?" rows={4} />
        </Field>
      </section>

      {/* Section: Slide Deck */}
      <section className={sectionClass}>
        <h3 className={headingClass}>Slide Deck <span className="text-muted normal-case text-sm font-sans font-normal tracking-normal">(Optional)</span></h3>
        {local.slideDeckUrl ? (
          <div className="flex items-center gap-3 p-4 bg-surface border border-line rounded-lg">
            <svg className="w-8 h-8 text-chart flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text truncate">{local.slideDeckName}</p>
              {local.slideDeckSize > 0 && (
                <p className="text-xs text-muted">{formatBytes(local.slideDeckSize)}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <a href={local.slideDeckUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="subtle" size="sm">View</Button>
              </a>
              {!readOnly && (
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={handleDeckDelete}
                  disabled={deckDeleting}
                >
                  {deckDeleting ? 'Removing…' : 'Remove'}
                </Button>
              )}
            </div>
          </div>
        ) : !readOnly ? (
          <div
            className="border-2 border-dashed border-line rounded-lg p-8 text-center cursor-pointer hover:border-chart/40 hover:bg-surface/50 transition-colors"
            onClick={() => fileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (file) handleDeckUpload(file);
            }}
          >
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPTED_DECK}
              className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) handleDeckUpload(f); }}
            />
            {deckUploading ? (
              <p className="text-sm text-muted">Uploading…</p>
            ) : (
              <>
                <svg className="w-8 h-8 text-muted mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
                <p className="text-sm text-muted">Drop a file or <span className="text-chart font-medium">click to upload</span></p>
                <p className="text-xs text-muted mt-1">PDF, PPT, or PPTX</p>
              </>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted italic">No slide deck attached.</p>
        )}
      </section>

      {/* Footer actions */}
      {!readOnly && (
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-line">
          {onSubmit ? (
            <>
              <Button variant="subtle" size="md" onClick={() => onSaveDraft(local)} disabled={saving}>
                {saving ? 'Saving…' : 'Save Draft'}
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => onSubmit({ ...local, isSubmitted: true })}
                disabled={saving}
              >
                {saving ? 'Submitting…' : 'Submit Pitch'}
              </Button>
            </>
          ) : (
            <Button variant="primary" size="md" onClick={() => onSaveDraft(local)} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default PitchForm;
